import {Button, Space, Table, Typography, Modal} from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { PaginationComponent } from "~Common/Pagination/PaginationComponent";
import PermissionCheck from "../../Common/PermissionCheck";
import SearchFilter from "~components/SearchFilter/SearchFilter";
import { useDeleteMatchProfile } from "../../entities/api";
import { useFetchMatchProfilesPagination } from "~entities/api/useMatchProfilesAPI";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useHandlePagination } from "~hooks/useHandlePagination";

const PermissionName = PermissionNames.MATCH_PROFILE;

const useGetColumns = ({deleteMatchProfile, modal}) => {
  const { formatMessage } = useIntl();
  return [
    {
      title: formatMessage({
        id: "name",
        defaultMessage: "Name",
      }),
      dataIndex: "name",
      width: "200px",
    },
    {
      title: "Default",
      dataIndex: "defaultProfile",
      render: (defaultProfile) => (defaultProfile ? <i className="fas fa-check" /> : ""),
    },
    {
      render: (_, record) => (
        <Space size={0}>
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.UPDATE]}>
            <Link to={`manage/${record?.profileId}`}>
              <Button type="text" color="primary" className={"text-uppercase"}>
                {formatMessage({
                  id: "manage",
                  defaultMessage: "Manage",
                })}
              </Button>
            </Link>
          </PermissionCheck>
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.DELETE]}>
            <Button
              type="text"
              onClick={() => {
                modal.confirm({
                  title: formatMessage({
                    id: "match.delete",
                    defaultMessage: "Delete Match Profile",
                  }),
                  content: formatMessage({
                    id: "match.profile.delete",
                    defaultMessage: "You are about to delete this Match Profile, is this OK?",
                  }),
                  okText: formatMessage({
                    id: "delete",
                    defaultMessage: "Delete",
                  }),
                  onOk: () => deleteMatchProfile(record?.profileId),
                  cancelButtonProps: { type: "text" },
                  okButtonProps: { type: "danger" },
                });
              }}
            >
              <i data-testid="delete-icon" className={`fas fa-trash-can`} />
            </Button>
          </PermissionCheck>
        </Space>
      ),
      width: 1,
    },
  ];
}

const MatchProfilesList = () => {
  const { formatMessage } = useIntl();
  const [searchText, setSearchText] = useState("");
  const [profilesListFiltered, setProfilesListFiltered] = useState(null);
  const { pageSize, currentPage, handlePaginationChange, offset, pageSizeOptions } = useHandlePagination();
  const { mutate: deleteFn, isPending } = useDeleteMatchProfile();
  const { data, isLoading } = useFetchMatchProfilesPagination({
    limit: pageSize,
    offset,
    nameContains: searchText,
  });
  const [modal, contextHolder] = Modal.useModal();

  const profilesList = useMemo(() => data?.data || [], [data?.data]);
  const nextLink = useMemo(() => data?.links?.next, [data?.links?.next]);

  const deleteMatchProfile = (profileId) => {
    deleteFn(profileId);
  };

  const columns = useGetColumns({deleteMatchProfile, modal})

  useEffect(() => {
    if (searchText?.length) {
      setProfilesListFiltered(
        profilesList?.filter((profile) => profile?.name.toLowerCase()?.includes(searchText?.toLowerCase())),
      );
    } else {
      setProfilesListFiltered(profilesList);
    }
  }, [searchText, profilesList]);

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
        className={"mb-2"}
      >
        <Typography.Title level={4}>
          {formatMessage({
            id: "match.profiles",
            defaultMessage: "Match Profiles",
          })}
        </Typography.Title>
        <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.CREATE]}>
          <Space>
            <SearchFilter doSearch={setSearchText} />
            <Link to={`manage`}>
              <Button type="primary" icon={<i className="fa-solid fa-plus mr-1"></i>}>
                Match Profile
              </Button>
            </Link>
          </Space>
        </PermissionCheck>
      </div>
      {contextHolder}
      <Table
        dataSource={profilesListFiltered}
        loading={isPending || isLoading}
        size={"small"}
        rowKey={({ profileId }) => profileId}
        pagination={{
          current: currentPage,
          defaultPageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions,
          showQuickJumper: false,
          total: nextLink ? offset + profilesList.length + 1 : profilesList.length + offset,
          position: ["bottomCenter"],
          onChange: handlePaginationChange,
          showTotal: (total, range) => (
            <PaginationComponent pageNumber={currentPage} pageSize={pageSize} total={total} range={range} />
          ),
        }}
        columns={columns}
      />
    </>
  );
};

export default MatchProfilesList;
